/**
 * The rules behind the Modules screens, kept out of the components so they can be tested alone:
 * what a start form sends, how a run's progress reads, what each gate needs, and how a refusal is
 * said to the person who hit it.
 */
import type { Schemas } from "@neio/contracts";
import { ApiError, switchedOff } from "../../lib/http";
import { fieldLabel, lifecycleLabel } from "../../lib/words";

export type NewRun = Schemas["NewRun"];
export type RunView = Schemas["RunView"];
export type ModuleView = Schemas["ModuleView"];

/** The request types a start form exists for. Each is the first request type of a catalog entry. */
export const STARTABLE = [
  "package.intelligence",
  "asset.identity",
  "asset.lifecycle",
  "maintenance.insights",
  "site.applicability",
  "spec.comparison",
  "spec.standardization",
] as const;
export type Startable = (typeof STARTABLE)[number];

export const AUTONOMY_MODES = [
  { value: "supervised", label: "Supervised", hint: "Stops for review on every tag." },
  { value: "exception", label: "By exception", hint: "Stops only when the evidence is weak or two sources disagree." },
  { value: "unattended", label: "Unattended", hint: "Never stops; weak tags are listed for review at the end." },
] as const;

export type StartForm = Partial<Record<keyof NewRun, string>>;
export type Started = { requestType: Startable; body: NewRun } | { problem: string };

const KEY = /^[^:\s]+:[^:\s]+$/;

/** The request a start form sends, or the one thing the person still has to fill in. */
export function startRequest(requestType: Startable, form: StartForm): Started {
  const v = (k: keyof NewRun) => (form[k] ?? "").trim();
  const body: NewRun = {};
  const need = (k: "package" | "tag" | "component" | "notice", problem: string) => {
    if (!v(k)) return problem;
    body[k] = v(k);
    return null;
  };
  const problems: (string | null)[] = [];

  switch (requestType) {
    case "package.intelligence": {
      problems.push(need("package", "Enter a package."));
      body.autonomy_mode = v("autonomy_mode") || "supervised";
      const limit = v("limit");
      if (limit) {
        if (!/^\d+$/.test(limit) || Number(limit) < 1) {
          problems.push("The limit is a whole number of tags, 1 or more.");
        } else {
          body.limit = Number(limit);
        }
      }
      break;
    }
    case "asset.identity":
    case "asset.lifecycle":
      problems.push(need("package", "Enter a package."), need("tag", "Enter a tag."));
      break;
    case "maintenance.insights":
      problems.push(
        need("package", "Enter a package."),
        need("component", "Enter the component, in the words a visit report uses."),
      );
      break;
    case "site.applicability":
      problems.push(need("package", "Enter a package."), need("notice", "Paste the bulletin text."));
      break;
    case "spec.comparison": {
      if (!KEY.test(v("old_key")) || !KEY.test(v("new_key"))) {
        problems.push("Write each register key as package:tag, for example MSO:PT-403.");
      } else {
        body.old_key = v("old_key");
        body.new_key = v("new_key");
      }
      if (v("equipment_class")) body.equipment_class = v("equipment_class");
      break;
    }
    case "spec.standardization":
      problems.push(need("package", "Enter a package."), need("tag", "Enter a tag."));
      if (v("equipment_class")) body.equipment_class = v("equipment_class");
      break;
  }
  const problem = problems.find((p) => p);
  return problem ? { problem } : { requestType, body };
}

export interface Refusal {
  /** `off`: this environment does not run the module. `limit`: a per-person or queue limit. */
  kind: "off" | "limit" | "refused";
  title: string;
  detail: string;
}

/** A refused start or decision, said plainly. The server's own words are kept where they help. */
export function refusal(error: unknown, action = "The run was not started"): Refusal {
  const message = error instanceof Error ? error.message : String(error);
  if (!(error instanceof ApiError)) return { kind: "refused", title: action, detail: message };
  if (error.status === 429) {
    if (/RATE_LIMIT_CONCURRENT_RUNS|package run\(s\) running/.test(message)) {
      return {
        kind: "limit",
        title: "You already have a package run working",
        detail: "Each person has one package run at a time. Wait for it to finish, or decide its gate, then start this one.",
      };
    }
    const perMinute = /more than (\d+) paid starts in a minute; try again in (\d+) seconds/.exec(message);
    if (perMinute) {
      return {
        kind: "limit",
        title: "Spend limit reached for this minute",
        detail: `More than ${perMinute[1]} paid starts in a minute. Try again in ${perMinute[2]} seconds.`,
      };
    }
    if (/queued/.test(message)) return { kind: "limit", title: "The work queue is full", detail: message };
    return { kind: "limit", title: "Too many requests", detail: message };
  }
  const off = action === "The run was not started" ? switchedOff(error, [409]) : null;
  if (off !== null) return { kind: "off", title: "Switched off on this environment", detail: off };
  if (error.status === 403) return { kind: "refused", title: "Outside your packages", detail: message };
  return { kind: "refused", title: action, detail: message };
}

export type Tone = "ok" | "review" | "fail" | "info";

export interface Progress {
  label: string;
  tone: Tone;
  /** Still working, so the detail screen keeps polling. */
  active: boolean;
  /** For a package run: tags finished, and tags the survey found. */
  done?: number;
  total?: number;
}

const GATE_STATUSES = new Set(["awaiting_confirmation", "awaiting_spec_approval", "awaiting_review"]);
const WORKING_STATUSES = new Set(["running", "reading", "researching"]);

const ENDED: Record<string, { label: string; tone: Tone }> = {
  completed: { label: "Finished", tone: "ok" },
  not_found: { label: "Nothing found", tone: "review" },
  abandoned: { label: "Declined at the gate", tone: "review" },
  budget_exhausted: { label: "Stopped at the spend ceiling", tone: "fail" },
  interrupted: { label: "Interrupted by a restart", tone: "review" },
  failed: { label: "Failed", tone: "fail" },
};

function packageCounts(run: RunView): { done?: number; total?: number } {
  const survey = (run.trail ?? []).find((s) => s.step === "survey");
  const total = typeof survey?.tags === "number" ? survey.tags : undefined;
  const outcomes = (run.findings?.outcomes ?? {}) as Record<string, string>;
  const done = Object.values(outcomes).filter((o) => o !== "not_attempted").length;
  return total === undefined ? {} : { done, total };
}

/** Where a run is, in words, and whether to keep asking. */
export function runProgress(run: RunView): Progress {
  const counts = packageCounts(run);
  if (WORKING_STATUSES.has(run.status) && run.work_state !== "failed") {
    const queued = run.work_state === "queued";
    return { label: queued ? "Waiting for a worker" : "Working", tone: "info", active: true, ...counts };
  }
  if (run.status === "awaiting_review") {
    // A package run's review of one tag, or a lifecycle finding to check with no gate open.
    return { label: "Needs a person", tone: "review", active: false, ...counts };
  }
  if (GATE_STATUSES.has(run.status)) {
    return { label: "Waiting for a decision", tone: "review", active: false, ...counts };
  }
  const ended = ENDED[run.status] ?? { label: lifecycleLabel(run.status), tone: "info" as Tone };
  return { ...ended, active: false, ...counts };
}

export type Decision = Schemas["Decision"];
export type Gate = "confirm_documents" | "approve_spec" | "identity_needs_review";
const GATES: readonly string[] = ["confirm_documents", "approve_spec", "identity_needs_review"];

/** The gate a run is waiting on, or null when it is working or has ended. */
export function gateOf(run: RunView): Gate | null {
  if (!run.gate || !GATES.includes(run.gate)) return null;
  return GATE_STATUSES.has(run.status) || run.status === "interrupted" ? (run.gate as Gate) : null;
}

export type Decided = { decision: Decision } | { problem: string };

/** A field's name as an engineer says it in a sentence: "manufacturer", "model number". */
export const fieldWord = (field: string) => fieldLabel(field).toLowerCase();
const label = fieldWord;

/** A field's name as a column heading: "Manufacturer", "Model number". */
export const fieldHeading = (field: string) => fieldLabel(field);

/** The autonomy check's reasons (`autonomy.is_strong`), said the way a person would say them. */
export function plainReason(reason: string): string {
  let m = /^(\w+) is not known$/.exec(reason);
  if (m) return `the ${fieldWord(m[1])} is not known yet`;
  m = /^documents disagree on (\w+)$/.exec(reason);
  if (m) return `two documents give different ${fieldWord(m[1])}s`;
  m = /^(\w+) is below the confidence floor$/.exec(reason);
  if (m) return `the ${fieldWord(m[1])} was read with too little confidence`;
  m = /^(\w+) rests on one source and one reader/.exec(reason);
  if (m) return `the ${fieldWord(m[1])} comes from one document read once, where a misread character could hide`;
  return reason;
}

/** "Stopped on BV-401: the manufacturer is not known yet". An unknown class is left out. */
export function stopHeadline(prompt: { tag?: string; reasons?: string[]; equipment_class?: string | null }): string {
  const klass = prompt.equipment_class && prompt.equipment_class !== "unknown" ? `, a ${prompt.equipment_class}` : "";
  const first = prompt.reasons?.[0];
  return `Stopped on ${prompt.tag ?? "this tag"}${klass}${first ? `: ${plainReason(first)}` : ""}`;
}

/** What a decision just did, for the line the run page shows after it. */
export function decidedMessage(d: Decision): string {
  const paths = d.paths ?? null;
  const tag = d.tag ?? null;
  if (paths && paths.length) {
    return `Approved. Reading ${paths.length} document${paths.length === 1 ? "" : "s"}; this page follows the run.`;
  }
  if (paths) return tag ? `Skipped ${tag}. The run moves on to the next tag.` : "Declined. The run stops here and reads nothing.";
  if (d.approved_by !== undefined && d.approved_by !== null) {
    return tag
      ? `Approved ${tag}. The run researches it, then moves on to the next tag.`
      : "Approved the specification. The lifecycle search is running.";
  }
  return "Resumed. The run carries on from where it stopped.";
}

const andList = (xs: string[]) => (xs.length < 2 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`);

/**
 * What a run that ended "Needs a person" is waiting on, by kind: the fields of an identity to
 * check, or a lifecycle finding. Null when it is neither.
 */
export function needsPersonText(run: RunView): string | null {
  const needs = ((run.asset as { needs_review?: string[] } | null | undefined)?.needs_review ?? []).map(fieldWord);
  if (needs.length) {
    const one = needs.length === 1;
    return `The ${andList(needs)} of ${run.tag ?? "this tag"} need${one ? "s" : ""} checking against the documents before anything relies on ${one ? "it" : "them"}.`;
  }
  const finding = run.findings as { status?: string; disagreement?: boolean } | null | undefined;
  if (finding?.status || finding?.disagreement) {
    return "This finding says the part is obsolete or announced, or its sources disagree, so a person checks it before a case is opened.";
  }
  return null;
}

function filled(values: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(values).map(([k, v]) => [k, v.trim()] as const).filter(([, v]) => v),
  );
}

/** Read these documents. */
export function documentsDecision(paths: string[]): Decided {
  if (paths.length === 0) return { problem: "Pick at least one document to read, or decline." };
  return { decision: { paths } };
}

/** Search on this spec. A field two documents disagree on needs a chosen value first. */
export function specDecision(unresolved: string[], overrides: Record<string, string>): Decided {
  const chosen = filled(overrides);
  const open = unresolved.filter((f) => !chosen[f]);
  if (open.length) {
    return { problem: `Two documents disagree on ${open.map(label).join(", ")}. Choose a value for each before approving.` };
  }
  // Empty: the server records the signed-in person as the approver, whatever the body says.
  return { decision: Object.keys(chosen).length ? { approved_by: "", overrides: chosen } : { approved_by: "" } };
}

/** Approve the identity of the tag a package run is reviewing. */
export function reviewDecision(tag: string, overrides: Record<string, string>): Decided {
  const chosen = filled(overrides);
  return { decision: { approved_by: "", tag, ...(Object.keys(chosen).length ? { overrides: chosen } : {}) } };
}

/** Decline at whichever gate the run is at. At a package review it names the tag, as approval does. */
export function decline(run: RunView): Decision {
  const tag = run.gate === "identity_needs_review" ? (run.gate_prompt?.tag as string | undefined) : undefined;
  return tag ? { paths: [], tag } : { paths: [] };
}

/** Carry on an interrupted run with no gate open. Says something, as a decision must, and approves nothing. */
export function resumeDecision(): Decision {
  return { overrides: {} };
}

/** Module names by request type, as the catalog names them, for lists drawn without the catalog. */
const NAMES: Record<string, string> = {
  "package.intelligence": "Package Intelligence",
  package: "Package Intelligence",
  "asset.identity": "Asset Identity",
  identity: "Asset Identity",
  "uc1.identity_lookup": "Asset Identity",
  "asset.lifecycle": "Lifecycle Intelligence",
  obsolescence: "Lifecycle Intelligence",
  "uc5.successor_search": "Lifecycle Intelligence",
  "maintenance.insights": "Maintenance History Intelligence",
  "site.applicability": "Site Applicability",
  "spec.comparison": "Old Against New Specification Comparison",
  "spec.standardization": "Specification Standardization",
};

export function moduleName(requestType: string): string {
  return NAMES[requestType.toLowerCase()] ?? requestType;
}
